import { Icon } from '@/components/icons/svg-icons';
import { OddsButton } from '@/components/widgets/odds-button';
import { betTypeService } from '@/services/bettype.service';
import { moreAisanLinesBetTypes } from '@/settings/bet-type-settings';
import { filterBetTypesFromMarkes, sortedMarketBetType } from '@/utils/odds-utils';
import { Market, SportMatchModel } from '@nf/types/odds';
import { Button } from '@nf/ui';
import { Maybe, IO, MaybeMatch } from '@nf/utils-common/fpw/monadic';
import { itemsEmptyCheck } from '@nf/utils-common/fpw/utils';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { useState, useEffect } from 'react';
import { matchStyles } from '../match/match.style';
import { MatchInfo } from '../odds-page-items';
import { useAsianLines } from './contexts';
import { css } from 'styled-system/css';
import { useGetBetTypeName, useGetBetTypeSetting, useGetGoal } from '@/hooks';
import { useOddsTableMarsContext } from '../../context';
import { useAnalyseAttribute } from '@/features/next-analyse';

interface AsianLinesProps {
    markets: Market[];
	match: SportMatchModel;
	homeTeamName: string;
	awayTeamName: string;
}
export const AsianLines = ({
	markets,
	match,
	homeTeamName,
	awayTeamName
}: AsianLinesProps) => {
	const { sport, oddsType, actStatus } = useOddsTableMarsContext();
	const { closeMoreAsianLines, isFullTime, isOpen } = useAsianLines();
	const [selectedBetType, setSelectedBetType] = useState<number>();
	const [asianLinesMarkets, setAsiaLineMarkets] = useState<Market[]>([]);
	const getBetTypeTitle = useGetBetTypeName();
	const getGoal = useGetGoal();
	const [moreAsianLinesBetTypes, setMoreAsianLinesBetTypes] = useState<number[]>([]);
	const t = useTranslations('General');
	const getBetTypeSetting = useGetBetTypeSetting();
	const analyseMoreLinesAttributeRef = useAnalyseAttribute('close-more-lines-button');


	useEffect(() => {
		Maybe(markets)
			.chain(() => itemsEmptyCheck(markets))
			.fold(
				() => [],
				() => IO.of(markets)
					.map(filterBetTypesFromMarkes(moreAisanLinesBetTypes, true))
					.map(sortedMarketBetType(moreAisanLinesBetTypes))
					.map(setAsiaLineMarkets)
					.run()
			)
	}, [markets])

	useEffect(() => {
		setMoreAsianLinesBetTypes(Array.from(new Set(asianLinesMarkets.map(market => market.BetTypeId))))
	}, [asianLinesMarkets])

	useEffect(() => {
		itemsEmptyCheck(moreAsianLinesBetTypes)
			.chain(() => MaybeMatch(isOpen))
			.fold(
				() => {},
				() => {
					setSelectedBetType(getDefaultBetType(moreAsianLinesBetTypes, isFullTime))
				}
			)
	}, [isOpen])

	useEffect(() => {
		!moreAsianLinesBetTypes.includes(selectedBetType ?? 0) && setSelectedBetType(getDefaultBetType(moreAsianLinesBetTypes, isFullTime))
	}, [moreAsianLinesBetTypes, selectedBetType])


	return (
		<div
			className={cx('more-asian-lines', matchStyles.moreLine)}
			data-active={isOpen}
		>
			<MatchInfo match={match} />
			<div className={matchStyles.oddsTable}>
				<div className={matchStyles.moreLineTab}>
					<div className={matchStyles.moreLineTabContainer}>
						{
							moreAsianLinesBetTypes.map(betType => {
								return (
									<div
										key={betType}
										className={matchStyles.moreLineTabItem}
										data-active={betType === selectedBetType}
										onClick={() => setSelectedBetType(betType)}
									>
										{ getBetTypeTitle(betType, 1)}
									</div>
								)
							})
						}
					</div>
				</div>
				<div className={css(matchStyles.bettypeWraper, matchStyles.moreLineBettypeWraper)}>
					<div className={css(matchStyles.bettypes, matchStyles.moreLineBettypes)}>
						{
							asianLinesMarkets.filter(market => market.BetTypeId === selectedBetType).map((market) => {
								const { betChoices } = getBetTypeSetting(market.BetTypeId, match.GameID);
								return (
									<div key={`${market.MatchId}_${market.MarketId}_${market.BetTypeId}`} className={css(matchStyles.bettype, matchStyles.moreLineBettype)}>
										{
											betChoices?.map((betChoice) => {
												const displayClassName = betTypeService.getBetChoiceDisplaySettingBetTypeClassName(market.BetTypeId, betChoice.betTeam);
												return (
													<OddsButton 
														key={`${market.MatchId}_${market.MarketId}_${market.BetTypeId}_${betChoice.betTeam}`}
														goal={getGoal(market, betChoice)}
														match={match}
														market={market}
														selection={market.Selections[betChoice.betTeam]}
														homeTeamName={homeTeamName}
														awayTeamName={awayTeamName}
														variant={displayClassName} 
														betTeam={market.Selections[betChoice.betTeam].SelId} 
														originalPrice={market.Selections[betChoice.betTeam].Price}
														sport={sport}
														type="OU"
														oddsType={oddsType}
														accountStatus={actStatus ?? 0}
													/>
												)
											})
										}
									</div>
								)
							})
						}
					</div>
				</div>
				<div className={matchStyles.moreLineFooter}>
					<Button
						ref={analyseMoreLinesAttributeRef}
						variant="primaryOutline"
						className={matchStyles.closeButton}
						onClick={() => closeMoreAsianLines()}
					>
						{ t('lbl_btnClose') }
						<Icon name="close-big" />
					</Button>
				</div>
			</div>
		</div>
	)
}

const getDefaultBetType = (betTypes: number[], isFullTime: boolean) => {
	const targetBetTypes = isFullTime ? [1, 3] : [7, 8];
	const intersectionBetTypes = betTypes.filter(betType => targetBetTypes.includes(betType))
	return intersectionBetTypes[0] ?? betTypes[0];
}